import { useRef, useState } from "react";
import { PhoneCall, ChevronDown, Video } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@primitives/dropdown-menu";
import { featureFlags } from "@/config/featureFlags";
import { createMeeting } from "@/server/services/meetingsService";
import { useRtfInputState } from "@/zustand/RtfInputState";
import { useNavigationState } from "@/zustand/NavigationState";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { copyTextToClipboard } from "@/lib/clipboard";
import { useToast } from "@/hooks/useToast";

interface CallSplitButtonProps {
  /** The active chat id (snapshot from CallButtons) the actions are scoped to. */
  chatId: string;
  /**
   * Existing Product-A start-call behavior (group or P2P). Owned by CallButtons
   * so all start-call logic stays in one place; the split button just triggers it.
   */
  onStartCall: () => void;
}

/**
 * Google-Chat-style split call button for the chat header (Meet UX Variant 1).
 *
 * Mounted by CallButtons ONLY when `featureFlags.orionMeetUi` is ON, so the
 * whole split UI rides that master kill-switch (prod/beta = off = legacy single
 * button). The primary (left) button starts a call in THIS chat — byte-identical
 * to the legacy control. The caret opens a menu with:
 *   - "Call this chat" — the same start-call action, and
 *   - "Send meeting link" (Product B) — additionally gated by
 *     `featureFlags.standaloneMeet`.
 *
 * "Send meeting link" creates a standalone meeting (POST /umbra/meet/meetings,
 * reused from #1077) and drops its shareable link into the composer draft when
 * the composer is EMPTY (it hydrates live via useRtfState's empty-draft branch).
 * When the user already has text typed, it COPIES the link to the clipboard
 * instead — a graceful, non-destructive fallback so the action never silently
 * no-ops and never clobbers an in-progress message.
 *
 * `meetingId` is a non-secret shareable slug by design (see
 * CreateMeetingResponse) so it is safe in the link path, unlike guest-invite
 * codes (which are secrets and ride the URL fragment). Backend errors are never
 * surfaced verbatim — only a generic toast.
 */
export default function CallSplitButton({
  chatId,
  onStartCall,
}: CallSplitButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  // Ref (not state) so a rapid second click is blocked synchronously, before
  // React re-renders with `disabled` — prevents a double-create / orphan meeting.
  const creatingRef = useRef(false);

  const handleSendMeetingLink = async () => {
    // Defense-in-depth: never reach POST /umbra/meet/meetings unless Product B
    // is enabled, independent of the menu-item render gate. Also guard reentry.
    if (!featureFlags.standaloneMeet || creatingRef.current) {
      return;
    }
    // Snapshot the chat id at click time so an in-flight create can't write the
    // link into a chat the user has since switched away from.
    const targetChatId = chatId;
    creatingRef.current = true;
    setCreating(true);
    try {
      const { meetingId } = await createMeeting();
      const link = `${window.location.origin}/meet/${meetingId}`;

      // Re-read LIVE state AFTER the await. Only drop the link straight into the
      // composer draft when the composer is genuinely FREE to receive it:
      //   - still the same active chat (user didn't switch away),
      //   - the text draft is empty,
      //   - no reply/edit in progress, AND
      //   - no media/file attachment staged.
      // These mirror exactly what RtfInput considers "has pending content"
      // (RtfInput's `canSendMessage` = draft text OR newFileAttachment OR
      // newMediaAttachmentUrl) plus the reply/edit modes — because useRtfState's
      // hydration is a strict edit → reply → draft precedence chain, so a draft
      // written while any of those is active would NEVER hydrate the textarea and
      // the link would be silently lost. NOTE (coupling): reading emptiness from
      // the draft STORE is a valid proxy for the editor's live `isEmpty` only
      // because useRtfEditor persists every keystroke to the draft store and
      // ChatPlayground remounts per active chat (key={activeChatId}); if either
      // changes, revisit this check. In every NOT-free case we copy the link to
      // the clipboard (visible, non-destructive) rather than a silent no-op.
      const rtf = useRtfInputState.getState();
      const stillActiveChat =
        useNavigationState.getState().activeChatId === targetChatId;
      const draft = rtf.draftMessages[targetChatId] ?? "";
      const hasStagedAttachment =
        !!useFileMessageSenderState.getState().newFileAttachment ||
        !!useMediaMessageSenderState.getState().newMediaAttachmentUrl;
      const composerFree =
        stillActiveChat &&
        draft.trim().length === 0 &&
        !rtf.replyMessages[targetChatId] &&
        !rtf.editMessages[targetChatId] &&
        !hasStagedAttachment;

      if (composerFree) {
        rtf.setDraftMessage(targetChatId, link);
      } else {
        const ok = await copyTextToClipboard(link);
        toast({
          // On failure, surface the link itself (longer-lived, selectable) so a
          // just-created meeting can never become unreachable if the clipboard
          // write is refused (e.g. Firefox loses transient activation post-await).
          duration: ok ? 1500 : 8000,
          description: ok
            ? t("standaloneMeet.copySuccess")
            : t("callMenu.copyFailedLink", { link }),
          variant: ok ? "default" : "destructive",
        });
      }
    } catch {
      toast({
        duration: 2000,
        description: t("standaloneMeet.createFailed"),
        variant: "destructive",
      });
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  // Self-gate on the master flag too (defense-in-depth): even though CallButtons
  // only mounts this when orionMeetUi is on, the component refuses to render any
  // Meet surface if that ever changes. Placed after all hooks (rules-of-hooks).
  if (!featureFlags.orionMeetUi) {
    return null;
  }

  return (
    <div className="flex select-none items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="size-8 cursor-pointer"
        aria-label={t("call")}
        onClick={onStartCall}
      >
        <PhoneCall size={20} />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-6 cursor-pointer"
            aria-label={t("callMenu.moreOptions")}
          >
            <ChevronDown size={16} />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="flex flex-col gap-0">
          <DropdownMenuItem
            onClick={onStartCall}
            className="flex items-center gap-2 lg:cursor-pointer"
          >
            <PhoneCall className="size-4" />
            {t("callMenu.callThisChat")}
          </DropdownMenuItem>

          {featureFlags.standaloneMeet && (
            <DropdownMenuItem
              onClick={handleSendMeetingLink}
              disabled={creating}
              className="flex items-center gap-2 lg:cursor-pointer"
            >
              <Video className="size-4" />
              {t("callMenu.sendMeetingLink")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
